import { makeStyles } from 'tss-react/mui';
import { alpha, Theme } from '@mui/material';

/**
 * Dialog Styles Hook
 * 
 * Provides Material-UI styles for dialog components used in the NN05 module.
 * This hook creates a comprehensive set of CSS-in-JS styles for modal dialogs,
 * overlays, and form components with responsive design and theme integration.
 * 
 * Features:
 * - Modal overlay with backdrop
 * - Responsive dialog positioning and sizing
 * - Sandwich menu integration with content shifting
 * - Form field styling with Material-UI theming
 * - Z-index management for proper layering
 * - Accessibility-focused design patterns
 * 
 * @returns {Object} An object containing all dialog-related CSS classes
 * 
 * @example
 * ```typescript
 * const { classes } = useStyles();
 * 
 * // Use in component
 * <div className={classes.component}>
 *   <div className={classes.bg} />
 *   <div className={classes.content}>
 *     <Paper className={classes.canvas}>
 *       // Dialog content
 *     </Paper>
 *   </div>
 * </div>
 * ```
 */
export const useStyles = makeStyles()((theme: Theme) => ({
  /**
   * Root container for dialog content
   * 
   * Creates a centered flex container that takes up most of the viewport.
   * Used as the main container for dialog content with proper positioning.
   * 
   * Properties:
   * - Full height (90vh) and width (100%)
   * - Centered content alignment
   * - Relative positioning for child elements
   */
  root: {
    display: 'flex', height: '90vh', width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    outline: 'none',
    '&:focus': {
      outline: 'none',
    },
  },

  /**
   * Background overlay/backdrop
   * 
   * Creates a semi-transparent overlay that covers the entire viewport.
   * Provides visual separation between dialog and background content.
   * 
   * Properties:
   * - Fixed positioning covering full viewport
   * - Semi-transparent gray background (rgba(50,50,50,0.8))
   * - High z-index (10000) to appear above content
   * - Pointer events enabled for interaction handling
   */
  bg: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    background: 'rgba(50,50,50,0.8)', // gray with transparency
    zIndex: 10000,
    pointerEvents: 'auto',
  },

  /**
   * Main dialog component container
   * 
   * Fixed positioned container that centers the dialog on screen.
   * Highest z-index to ensure dialog appears above all other content.
   * 
   * Properties:
   * - Fixed positioning covering full viewport
   * - Centered flex layout
   * - Highest z-index (20000) for top-level display
   * - Pointer events enabled for dialog interaction
   */
  component: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',

    zIndex: 20000, // higher than other content
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'auto', // allow interaction with overlay
  },

  /**
   * Main dialog canvas/paper container
   * 
   * The actual dialog box with defined dimensions and styling.
   * Used for larger dialogs like forms or detailed content.
   * 
   * Properties:
   * - Fixed width (600px) with minimum width (400px)
   * - Half viewport height (50vh)
   * - White background with full opacity
   * - Centered content layout
   * - Minimum height (600px) for consistent sizing
   */
  canvas: {
    width: 600,
    minWidth: 400,
    height: '50vh',
    zIndex: 10001,
    backgroundColor: alpha('#fff', 1),
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 600, // optional: ensures some height for centering
  },

  /**
   * Compact dialog canvas for simple dialogs
   * 
   * Similar to canvas but with fixed height for smaller dialogs.
   * Used for confirmation dialogs, alerts, or simple messages.
   * 
   * Properties:
   * - Same width constraints as canvas (600px/400px min)
   * - Fixed height (300px) for compact dialogs
   * - Same styling and positioning as canvas
   */
  dialogCanvas: {
    width: 600,
    minWidth: 400,
    height: '300px',
    zIndex: 10001,
    backgroundColor: alpha('#fff', 1),
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 600, // optional: ensures some height for centering
  },

  /**
   * Content container with responsive behavior
   * 
   * Main content area that adjusts based on navigation state.
   * Provides smooth transitions when sandwich menu is toggled.
   * 
   * Properties:
   * - Top margin (45px) for header clearance
   * - Flexible growth to fill available space
   * - Smooth transition animation using Material-UI theme
   * - Sharp easing for leaving screen transition
   */
  content: {
    marginTop: 45,
    flexGrow: 1,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  /**
   * Content container shifted state for sandwich menu
   * 
   * Modified content layout when sandwich/hamburger menu is open.
   * Shifts content to the right to accommodate navigation drawer.
   * 
   * Properties:
   * - Same top margin (45px) as content
   * - Left margin (340px) to accommodate drawer width
   * - Smooth transition with ease-out easing
   * - Entering screen transition duration
   */
  contentShift: {
    marginTop: 45,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 340,
  },

  /**
   * Text field styling for dialog forms
   * 
   * Comprehensive styling for Material-UI TextField components
   * used within dialogs. Provides consistent appearance and behavior.
   * 
   * Properties:
   * - Flex display with semi-transparent background
   * - Custom label styling (20px font, 45px height)
   * - Fixed height (45px) for consistent field sizing
   * - Custom outline and border styling
   * - Hover and focus state styling
   * - Black text color with proper contrast
   * - Focus state with increased opacity background
   */
  tField: {
    display: 'flex',
    backgroundColor: alpha('#fff', 0.4),
    '& label': { fontSize: 20, height: 45, lineHeight: .8, backgroundColor: '1px solid #4f4d4c', },
    height: 45,
    '& .MuiInput-underline:after': { height: 45, width: 0, fontSize: 20 },
    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { fontSize: 20, border: '1px solid #c4c4c4', },
    '& .MuiOutlinedInput-root': {
      color: 'black', height: 47, fontSize: 20,
      '& fieldset': { border: '2px solid white', },
      '&:hover fieldset': {},
      '&.Mui-focused ': { backgroundColor: alpha('#fff', 0.8), },
    },
  },
}),
);